import { useEffect, useState } from 'react';
import StatusBadge from '../components/StatusBadge';
import { Bouton, BoutonRetour, BoutonTheme, Capsule, Groupe, Icone } from '../components/ui';
import { listerToutesEnAttente, surChangementFile, synchroniser } from '../offline/syncManager';
import { supprimerDeLaFile } from '../offline/db';
import { heureLocale } from '../utils/dates';
import useBreakpoint from '../utils/useBreakpoint';
import {
  AMBRE, AMBRE_BG, AMBRE_FG, FOND_APP, OMBRE_CARTE, RAYON_CARTE, ROUGE_FG, SEPARATEUR, SURFACE, TEXTE,
  TEXTE_MUET, TEXTE_SECONDAIRE, VERT,
} from '../theme';

export default function SyncScreen({ enLigne, onFermer }) {
  const [file, setFile] = useState([]);
  const [reessaiEnCours, setReessaiEnCours] = useState(false);
  const { compact, large } = useBreakpoint();

  useEffect(() => {
    rafraichir();
    return surChangementFile(rafraichir);
  }, []);

  function rafraichir() {
    listerToutesEnAttente().then(setFile);
  }

  async function envoyerMaintenant() {
    setReessaiEnCours(true);
    await synchroniser();
    rafraichir();
    setReessaiEnCours(false);
  }

  const enAttente = file.filter((i) => !i.erreur);
  const enErreur = file.filter((i) => i.erreur);

  const etat = !enLigne
    ? { titre: 'Hors réseau', texte: `Hors ligne · ${enAttente.length} en attente`, point: AMBRE, desc: "La saisie continue à l'identique. Rien n'est bloqué, rien n'est perdu." }
    : enAttente.length > 0
    ? { titre: 'Envoi en cours', texte: `${enAttente.length} mesure(s) en attente`, point: AMBRE, desc: "Les mesures sont sur la tablette. L'envoi repart seul, sans intervention." }
    : { titre: 'Réseau disponible', texte: 'En ligne · tout est envoyé', point: VERT, desc: "Aucune action attendue de l'opérateur. Le badge reste discret en bas de l'écran de saisie." };

  // Trois cartes d'état côte à côte en large, deux en moyen, empilées en compact.
  const colonnesEtat = large ? 3 : compact ? 1 : 2;
  const marge = compact ? 16 : 28;

  return (
    <div style={{ height: '100dvh', overflow: 'auto', background: FOND_APP, color: TEXTE }}>
      <div className="aqs-materiau" style={{ position: 'sticky', top: 0, zIndex: 20, minHeight: 56, padding: `6px ${marge}px`, display: 'flex', alignItems: 'center', borderBottom: `0.5px solid ${SEPARATEUR}` }}>
        <BoutonRetour libelle="Retour" onClick={onFermer} />
        <div style={{ flex: 1 }} />
        <BoutonTheme />
      </div>

      <div className="aqs-nav-pousser" style={{ width: '100%', maxWidth: 1100, margin: '0 auto', padding: `16px ${marge}px 40px`, display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div>
          <h1 className="aqs-grand-titre" style={{ margin: 0 }}>Synchronisation</h1>
          <p className="aqs-para" style={{ margin: '6px 0 0', fontSize: 16, color: TEXTE_SECONDAIRE, maxWidth: 720 }}>
            Le badge est permanent en bas de l'écran de saisie. Il informe, il n'interrompt jamais : aucune mesure n'est perdue et rien n'est bloqué hors réseau.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${colonnesEtat}, minmax(0, 1fr))`, gap: 14, alignItems: 'stretch' }}>
          <CarteEtat titre={etat.titre}>
            <StatusBadge point={etat.point} texte={etat.texte} />
            <div className="aqs-para" style={{ fontSize: 15, lineHeight: 1.45, color: TEXTE_SECONDAIRE }}>{etat.desc}</div>
          </CarteEtat>

          <CarteEtat titre="File d'attente">
            <div style={{ fontSize: 40, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1 }}>{enAttente.length}</div>
            <div className="aqs-para" style={{ fontSize: 15, lineHeight: 1.45, color: TEXTE_SECONDAIRE }}>
              {enAttente.length === 0 ? 'Aucune mesure en attente d’envoi.' : 'Mesure(s) enregistrée(s) sur la tablette, en attente d’envoi.'}
            </div>
          </CarteEtat>

          <CarteEtat titre="Refusées par le serveur">
            <div style={{ fontSize: 40, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1, color: enErreur.length > 0 ? ROUGE_FG : undefined }}>{enErreur.length}</div>
            <div className="aqs-para" style={{ fontSize: 15, lineHeight: 1.45, color: enErreur.length > 0 ? ROUGE_FG : TEXTE_SECONDAIRE }}>
              {enErreur.length === 0 ? 'Aucun refus : tout ce qui est parti a été accepté.' : 'À écarter ou à ressaisir — le détail figure dans la file ci-dessous.'}
            </div>
          </CarteEtat>
        </div>

        {!enLigne && (
          <div className="aqs-apparition" style={{ background: AMBRE_BG, borderRadius: 14, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ color: AMBRE, display: 'inline-flex' }}><Icone.Nuage taille={22} /></span>
            <span className="aqs-para" style={{ minWidth: 0, fontSize: 15, color: AMBRE_FG }}>
              <strong style={{ fontWeight: 650 }}>Mode hors-ligne.</strong> Vos mesures sont enregistrées sur la tablette et envoyées dès le retour du réseau. Continuez à mesurer normalement.
            </span>
          </div>
        )}

        <div>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '0 4px 8px' }}>
            <span className="aqs-entete-groupe">File d'attente · {file.length} mesure(s)</span>
            <Bouton
              variante="teinte"
              onClick={envoyerMaintenant}
              disabled={!enLigne || reessaiEnCours || enAttente.length === 0}
            >
              {reessaiEnCours ? 'Envoi…' : 'Envoyer maintenant'}
            </Bouton>
          </div>
          <Groupe>
            {file.length === 0 && (
              <div style={{ padding: '18px 18px', fontSize: 15, color: TEXTE_MUET }}>Aucune mesure en attente.</div>
            )}
            {file.map((f, i) => (
              <div
                key={f.localId}
                className="aqs-apparition"
                style={{ position: 'relative', minHeight: 60, padding: compact ? '12px 16px' : '12px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: compact ? 'wrap' : 'nowrap' }}
              >
                {i > 0 && <div style={{ position: 'absolute', top: 0, left: 18, right: 0, height: 0.5, background: SEPARATEUR }} />}
                <Capsule ton={f.mode === 'update' ? 'accent' : 'neutre'}>{f.mode === 'update' ? 'Correction' : 'Nouvelle'}</Capsule>
                <span className="aqs-para" style={{ flex: 1, minWidth: 0, fontSize: 16 }}>
                  Taille <strong style={{ fontWeight: 650 }}>{f.payload.size}</strong> · {f.payload.values.length} cote(s)
                  {f.erreur && <span style={{ display: 'block', marginTop: 2, fontSize: 14, color: ROUGE_FG }}>{f.erreur}</span>}
                </span>
                <span style={{ flex: 'none', fontSize: 15, color: TEXTE_MUET }}>{heureLocale(f.creeLe)}</span>
                {f.erreur ? (
                  <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Capsule ton="rouge" point>Refusée</Capsule>
                    <Bouton variante="danger" hauteur={36} onClick={() => supprimerDeLaFile(f.localId).then(rafraichir)} style={{ fontSize: 14, padding: '0 12px', borderRadius: 18 }}>
                      Écarter
                    </Bouton>
                  </div>
                ) : (
                  <Capsule ton="orange" point>En attente</Capsule>
                )}
              </div>
            ))}
          </Groupe>
        </div>
      </div>
    </div>
  );
}

/** Carte d'état de la rangée de tête : hauteur libre, contenu espacé par `gap`. */
function CarteEtat({ titre, children }) {
  return (
    <div style={{ minWidth: 0, background: SURFACE, borderRadius: RAYON_CARTE, boxShadow: OMBRE_CARTE, padding: 20, display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'flex-start' }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: TEXTE_SECONDAIRE }}>{titre}</div>
      {children}
    </div>
  );
}
